// src/pages/TechStats.js
import React, { useEffect, useMemo, useState } from "react";
import { useROStore } from "../context/ROContext";
import { useAuth } from "../context/AuthContext";
import { RO_STATUS, RO_LIFECYCLE } from "../constants/roStatus";

/* ===============================
   Helpers
================================ */
function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function safeNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function safeDate(input) {
  if (!input) return null;
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toTime(input) {
  const d = safeDate(input);
  return d ? d.getTime() : null;
}

function hoursBetween(startISO, endISO) {
  const a = safeDate(startISO);
  const b = safeDate(endISO);
  if (!a || !b) return 0;

  const ms = b.getTime() - a.getTime();
  return ms > 0 ? ms / 36e5 : 0;
}

function formatHours(value) {
  const n = safeNumber(value);
  if (!n) return "0.0";
  return n.toFixed(1);
}

function formatEfficiency(ratio) {
  const n = Number(ratio);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

function normalizeName(v) {
  return safeString(v).trim().toLowerCase();
}

function normalizeRole(v) {
  const r = safeString(v).trim().toLowerCase();
  if (r === "tech") return "technician";
  if (r === "service_manager") return "manager";
  if (r === "store_manager") return "manager";
  return r;
}

function normalizeLifecycle(v) {
  const s = safeString(v).toUpperCase();

  if (s === "WAITING_ON_PARTS") return RO_LIFECYCLE.WAITING_FOR_PARTS;
  if (s === "WAITING_FOR_PARTS") return RO_LIFECYCLE.WAITING_FOR_PARTS;
  if (s === "WAITING_FOR_ASSIGNMENT") return RO_LIFECYCLE.WAITING_FOR_ASSIGNMENT;
  if (s === "IN_PROGRESS") return RO_LIFECYCLE.IN_PROGRESS;
  if (s === "COMPLETED") return RO_LIFECYCLE.COMPLETED;
  if (s === "OPEN") return RO_LIFECYCLE.OPEN;

  return s || RO_LIFECYCLE.OPEN;
}

function isCompletedLike(ro) {
  const status = safeString(ro?.status).toUpperCase();
  const lifecycle = normalizeLifecycle(ro?.lifecycle || ro?.lifeCycle);
  const completedKey = safeString(RO_STATUS?.COMPLETED || "COMPLETED").toUpperCase();

  return status === completedKey || lifecycle === RO_LIFECYCLE.COMPLETED;
}

function dateInputValue(input = new Date()) {
  const d = safeDate(input) || new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function startOfLocalDay(input = new Date()) {
  const d = safeDate(input) || new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(input, days) {
  const d = new Date(input);
  d.setDate(d.getDate() + days);
  return d;
}

function startOfLocalWeekMonday(input = new Date()) {
  const d = startOfLocalDay(input);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(d, diff);
}

function localDateFromInput(value) {
  const s = safeString(value).trim();
  if (!s) return null;

  const [year, month, day] = s.split("-").map((part) => Number(part));
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
}

const DATE_FILTERS = Object.freeze({
  LAST_7_DAYS: "LAST_7_DAYS",
  TODAY: "TODAY",
  YESTERDAY: "YESTERDAY",
  THIS_WEEK: "THIS_WEEK",
  THIS_MONTH: "THIS_MONTH",
  CUSTOM: "CUSTOM",
});

function getTechDateWindow(filter, customFrom, customTo) {
  const todayStart = startOfLocalDay();
  const tomorrowStart = addDays(todayStart, 1);

  if (filter === DATE_FILTERS.TODAY) {
    return {
      start: todayStart,
      end: tomorrowStart,
      label: "Today",
    };
  }

  if (filter === DATE_FILTERS.YESTERDAY) {
    return {
      start: addDays(todayStart, -1),
      end: todayStart,
      label: "Yesterday",
    };
  }

  if (filter === DATE_FILTERS.THIS_WEEK) {
    return {
      start: startOfLocalWeekMonday(),
      end: tomorrowStart,
      label: "This Week",
    };
  }

  if (filter === DATE_FILTERS.THIS_MONTH) {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth(), 1),
      end: tomorrowStart,
      label: "This Month",
    };
  }

  if (filter === DATE_FILTERS.CUSTOM) {
    const start = localDateFromInput(customFrom);
    const toStart = localDateFromInput(customTo);
    const end = toStart ? addDays(toStart, 1) : null;

    return {
      start,
      end,
      label:
        customFrom && customTo
          ? `${customFrom} to ${customTo}`
          : "Custom Range",
    };
  }

  return {
    start: addDays(todayStart, -6),
    end: tomorrowStart,
    label: "Last 7 Days",
  };
}

function isWithinDateWindow(input, dateWindow) {
  const t = toTime(input);
  const start = dateWindow?.start?.getTime?.();
  const end = dateWindow?.end?.getTime?.();

  if (!Number.isFinite(t)) return false;
  if (!Number.isFinite(start) || !Number.isFinite(end)) return true;

  return t >= start && t < end;
}

function getCompletedAt(ro) {
  return (
    ro?.timestamps?.completedAt ||
    ro?.timestamps?.completed_at ||
    ro?.timestamps?.technicianCompletedAt ||
    ro?.completedAtISO ||
    ro?.completed_at_iso ||
    ro?.completedAt ||
    ro?.completed_at ||
    ro?.technicianCompletedAtISO ||
    ro?.technician_completed_at_iso ||
    ro?.closedAt ||
    ro?.closed_at ||
    ""
  );
}

function getWorkedHours(ro) {
  const workedSeconds = Number(ro?.timestamps?.workedSeconds);
  if (Number.isFinite(workedSeconds) && workedSeconds > 0) return workedSeconds / 3600;

  const start =
    ro?.timestamps?.workStartAt ||
    ro?.timestamps?.startedAt ||
    ro?.workStartAt ||
    ro?.startedAt ||
    "";

  const end =
    ro?.timestamps?.workEndAt ||
    ro?.timestamps?.completedAt ||
    ro?.workEndAt ||
    ro?.completedAtISO ||
    ro?.completedAt ||
    "";

  return hoursBetween(start, end);
}

function getFlaggedHours(ro) {
  return (
    safeNumber(ro?.flaggedHours) ||
    safeNumber(ro?.hoursSold) ||
    safeNumber(ro?.soldHours) ||
    safeNumber(ro?.laborHoursSold) ||
    safeNumber(ro?.totalHoursSold)
  );
}

function getWorkMixKey(ro) {
  const workMix = safeString(ro?.workMix).toUpperCase().trim();
  if (workMix === "MAINTENANCE" || workMix === "DIAGNOSIS" || workMix === "OTHER") {
    return workMix;
  }

  const legacy = safeString(
    ro?.concern || ro?.jobType || ro?.concernType || ""
  ).toUpperCase();

  if (legacy.includes("MAINTENANCE")) return "MAINTENANCE";
  if (
    legacy.includes("DIAGNOSIS") ||
    legacy.includes("DIAG") ||
    legacy.includes("CHECK ENGINE")
  ) {
    return "DIAGNOSIS";
  }

  return "OTHER";
}

function workMixLabel(key) {
  if (key === "MAINTENANCE") return "Maintenance";
  if (key === "DIAGNOSIS") return "Diagnosis";
  return "Other";
}

function percent(numerator, denominator) {
  const n = safeNumber(numerator);
  const d = safeNumber(denominator);
  if (!d) return 0;
  return Math.round((n / d) * 100);
}

function techDisplayName(tech) {
  return safeString(tech?.name || tech?.fullName || tech?.displayName || "").trim();
}

function roAssignedTechName(ro) {
  return safeString(
    ro?.assignedTechName ||
      ro?.technicianName ||
      ro?.techName ||
      ro?.assigned_tech_name ||
      ""
  ).trim();
}

function roAssignedTechId(ro) {
  return safeString(
    ro?.assignedTechId ||
      ro?.technicianId ||
      ro?.techId ||
      ro?.assigned_tech_id ||
      ""
  ).trim();
}

function techMatchesRO(ro, selectedTechId, selectedTechName) {
  const roTechId = roAssignedTechId(ro);
  const roTechName = roAssignedTechName(ro);

  if (safeString(selectedTechId).trim() && roTechId) {
    return roTechId === safeString(selectedTechId).trim();
  }

  if (normalizeName(selectedTechName) && normalizeName(roTechName)) {
    return normalizeName(roTechName) === normalizeName(selectedTechName);
  }

  return false;
}

function getTag(ro) {
  return safeString(
    ro?.tagNumber ??
      ro?.tag ??
      ro?.keyTag ??
      ro?.key_tag ??
      ro?.tagId ??
      ro?.tag_id ??
      ""
  ).trim();
}

function formatDateTime(input) {
  const d = safeDate(input);
  if (!d) return "—";

  return `${d.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  })} ${d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  })}`;
}

function dedupeByRO(rows) {
  const seen = new Set();
  const out = [];

  rows.forEach((ro, index) => {
    const key = safeString(ro?.id || ro?.roNumber || ro?.ro_number || index);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(ro);
  });

  return out;
}

/* ===============================
   Styles
================================ */
const page = {
  padding: 12,
  display: "grid",
  gap: 10,
  maxWidth: 1500,
  margin: "0 auto",
};

const headerRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
};

const eyebrow = {
  fontSize: 10,
  fontWeight: 900,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "rgba(15,23,42,0.52)",
  marginBottom: 3,
};

const title = {
  margin: 0,
  fontSize: 22,
  lineHeight: 1.02,
  fontWeight: 700,
  letterSpacing: "-0.035em",
  color: "rgba(15,23,42,0.94)",
};

const subtitle = {
  margin: "4px 0 0",
  fontSize: 11.5,
  color: "rgba(15,23,42,0.58)",
  lineHeight: 1.32,
};

const card = {
  background: "#ffffff",
  border: "1px solid rgba(15,23,42,0.08)",
  borderRadius: 14,
  padding: 11,
  boxShadow: "0 6px 18px rgba(15,23,42,0.04)",
};

const metricGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 8,
};

const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 8,
};

const metricLabel = {
  fontSize: 10.2,
  fontWeight: 850,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "rgba(15,23,42,0.48)",
};

const metricValue = {
  marginTop: 3,
  fontSize: 20,
  lineHeight: 1,
  fontWeight: 800,
  color: "rgba(15,23,42,0.92)",
  letterSpacing: "-0.025em",
};

const metricSub = {
  marginTop: 5,
  fontSize: 10.8,
  color: "rgba(15,23,42,0.55)",
  lineHeight: 1.28,
};

const sectionTitle = {
  fontSize: 10.5,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "rgba(15,23,42,0.72)",
  marginBottom: 7,
};

const smallSelect = {
  height: 34,
  padding: "0 10px",
  borderRadius: 9,
  border: "1px solid rgba(15,23,42,0.10)",
  background: "#ffffff",
  color: "rgba(15,23,42,0.90)",
  fontSize: 12.2,
  fontWeight: 650,
  fontFamily: "inherit",
  minWidth: 210,
  outline: "none",
};

function metricCardStyle(tone = "default") {
  let borderColor = "#e5e7eb";
  let background = "#ffffff";

  if (tone === "danger") {
    borderColor = "#fecaca";
    background = "#fffafa";
  }

  if (tone === "warn") {
    borderColor = "#fed7aa";
    background = "#fff7ed";
  }

  if (tone === "good") {
    borderColor = "#bbf7d0";
    background = "#f0fdf4";
  }

  if (tone === "blue") {
    borderColor = "#bfdbfe";
    background = "#eff6ff";
  }

  return {
    ...card,
    borderColor,
    background,
    minHeight: 62,
  };
}

function pillStyle(tone = "default") {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 24,
    padding: "2px 8px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(248,250,252,0.94)",
    color: "rgba(15,23,42,0.68)",
    fontSize: 10.5,
    fontWeight: 900,
    whiteSpace: "nowrap",
  };

  if (tone === "danger") {
    return {
      ...base,
      borderColor: "rgba(180,35,24,0.14)",
      background: "rgba(254,243,242,0.96)",
      color: "#b42318",
    };
  }

  if (tone === "warn") {
    return {
      ...base,
      borderColor: "rgba(181,71,8,0.14)",
      background: "rgba(255,247,237,0.94)",
      color: "#b54708",
    };
  }

  if (tone === "good") {
    return {
      ...base,
      borderColor: "rgba(6,118,71,0.14)",
      background: "rgba(236,253,243,0.94)",
      color: "#067647",
    };
  }

  if (tone === "blue") {
    return {
      ...base,
      borderColor: "rgba(37,99,235,0.14)",
      background: "rgba(239,246,255,0.94)",
      color: "#1d4ed8",
    };
  }

  return base;
}

function ProgressBar({ value }) {
  const pct = Math.max(0, Math.min(100, safeNumber(value)));

  return (
    <div
      style={{
        height: 6,
        borderRadius: 999,
        background: "#eef2f7",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: "100%",
          borderRadius: 999,
          background: "#0f172a",
        }}
      />
    </div>
  );
}

/* ===============================
   Component
================================ */
export default function TechStats() {
  const store = useROStore();
  const auth = useAuth();

  const baseROs = useMemo(() => {
    if (Array.isArray(store?.repairOrders)) return store.repairOrders;
    if (Array.isArray(store?.ros)) return store.ros;
    return [];
  }, [store?.repairOrders, store?.ros]);

  const ros = useMemo(() => {
    const completedROs = Array.isArray(store?.completedROs) ? store.completedROs : [];
    const historyROs = Array.isArray(store?.historyROs) ? store.historyROs : [];
    return dedupeByRO([...baseROs, ...completedROs, ...historyROs]);
  }, [baseROs, store?.completedROs, store?.historyROs]);

  const techOptions = useMemo(() => {
    const map = new Map();
    const addTech = (id, name) => {
      const cleanName = safeString(name).trim();
      const cleanId = safeString(id || cleanName).trim();
      if (!cleanId && !cleanName) return;
      map.set(cleanId || cleanName, {
        id: cleanId || cleanName,
        name: cleanName || "Technician",
      });
    };

    const storeTechs = Array.isArray(store?.techs)
      ? store.techs
      : Array.isArray(store?.technicians)
      ? store.technicians
      : [];

    storeTechs.forEach((tech) => addTech(tech?.id, techDisplayName(tech)));
    ros.forEach((ro) => addTech(roAssignedTechId(ro), roAssignedTechName(ro)));

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [store?.techs, store?.technicians, ros]);

  const authUser = auth?.user || {};
  const authMembership = auth?.membership || {};

  const currentUser = useMemo(() => {
    return {
      id: safeString(authUser?.id),
      name: safeString(authUser?.name),
      role: safeString(authMembership?.role || authUser?.role),
    };
  }, [authUser?.id, authUser?.name, authUser?.role, authMembership?.role]);

  const role = normalizeRole(currentUser?.role);
  const isTech = role === "technician";
  const canCompare =
    role === "manager" ||
    role === "foreman" ||
    role === "dispatcher" ||
    role === "admin";

  const [selectedTechId, setSelectedTechId] = useState("");
  const [dateFilter, setDateFilter] = useState(DATE_FILTERS.LAST_7_DAYS);
  const [customFromDate, setCustomFromDate] = useState(() => dateInputValue(addDays(new Date(), -6)));
  const [customToDate, setCustomToDate] = useState(() => dateInputValue());

  useEffect(() => {
    if (isTech) {
      const currentName = normalizeName(currentUser?.name);
      const matchingTech = techOptions.find(
        (tech) =>
          safeString(tech.id) === safeString(currentUser?.id) ||
          normalizeName(tech.name) === currentName
      );

      setSelectedTechId(safeString(matchingTech?.id || currentUser?.id || ""));
      return;
    }

    setSelectedTechId((prev) => {
      if (prev && techOptions.some((tech) => safeString(tech.id) === safeString(prev))) {
        return prev;
      }
      return safeString(techOptions?.[0]?.id || "");
    });
  }, [isTech, currentUser?.id, currentUser?.name, techOptions]);

  const dateWindow = useMemo(
    () => getTechDateWindow(dateFilter, customFromDate, customToDate),
    [dateFilter, customFromDate, customToDate]
  );

  const selectedTech = useMemo(() => {
    return techOptions.find((tech) => safeString(tech.id) === safeString(selectedTechId)) || null;
  }, [techOptions, selectedTechId]);

  const techName = useMemo(() => {
    if (isTech) return safeString(currentUser?.name || selectedTech?.name || "Technician");
    return safeString(selectedTech?.name || "Technician");
  }, [isTech, currentUser?.name, selectedTech]);

  const metrics = useMemo(() => {
    const completed = ros.filter((ro) => {
      if (!isCompletedLike(ro)) return false;
      if (!techMatchesRO(ro, selectedTechId, techName)) return false;
      return isWithinDateWindow(getCompletedAt(ro), dateWindow);
    });

    const completedCount = completed.length;
    const flaggedHours = completed.reduce((sum, ro) => sum + getFlaggedHours(ro), 0);
    const workedHours = completed.reduce((sum, ro) => sum + getWorkedHours(ro), 0);
    const efficiency = workedHours > 0 ? flaggedHours / workedHours : 0;
    const avgFlagged = completedCount > 0 ? flaggedHours / completedCount : 0;
    const avgWorked = completedCount > 0 ? workedHours / completedCount : 0;

    const workMixCounts = completed.reduce(
      (acc, ro) => {
        const key = getWorkMixKey(ro);
        if (key === "MAINTENANCE") acc.Maintenance += 1;
        else if (key === "DIAGNOSIS") acc.Diagnosis += 1;
        else acc.Other += 1;
        return acc;
      },
      { Maintenance: 0, Diagnosis: 0, Other: 0 }
    );

    const recentCompleted = [...completed]
      .sort((a, b) => {
        const aTime = toTime(getCompletedAt(a)) || 0;
        const bTime = toTime(getCompletedAt(b)) || 0;
        return bTime - aTime;
      })
      .slice(0, 20);

    return {
      completed,
      completedCount,
      flaggedHours,
      workedHours,
      efficiency,
      avgFlagged,
      avgWorked,
      workMixCounts,
      recentCompleted,
    };
  }, [ros, selectedTechId, techName, dateWindow]);

  const efficiencyTone =
    metrics.workedHours > 0 && metrics.efficiency >= 1
      ? "good"
      : metrics.workedHours > 0
      ? "blue"
      : "default";

  return (
    <div style={page}>
      <div style={headerRow}>
        <div>
          <div style={eyebrow}>Technician Performance</div>
          <h1 style={title}>Technician Stats</h1>
          <div style={subtitle}>
            {canCompare
              ? "View individual technician performance, completed work, flagged hours, and efficiency."
              : "Your completed work, flagged hours, and efficiency."}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          <select
            value={selectedTechId}
            disabled={isTech}
            onChange={(e) => setSelectedTechId(e.target.value)}
            style={{
              ...smallSelect,
              background: isTech ? "#f8fafc" : "#ffffff",
              opacity: isTech ? 0.88 : 1,
            }}
          >
            {isTech ? (
              <option value={selectedTechId || safeString(currentUser?.id)}>
                {safeString(currentUser?.name || techName || "Technician")}
              </option>
            ) : (
              techOptions.map((tech) => (
                <option key={tech.id} value={tech.id}>
                  {tech.name}
                </option>
              ))
            )}
          </select>

          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            style={{ ...smallSelect, minWidth: 170 }}
          >
            <option value={DATE_FILTERS.LAST_7_DAYS}>Date: Last 7 Days</option>
            <option value={DATE_FILTERS.TODAY}>Date: Today</option>
            <option value={DATE_FILTERS.YESTERDAY}>Date: Yesterday</option>
            <option value={DATE_FILTERS.THIS_WEEK}>Date: This Week</option>
            <option value={DATE_FILTERS.THIS_MONTH}>Date: This Month</option>
            <option value={DATE_FILTERS.CUSTOM}>Date: Custom</option>
          </select>

          {dateFilter === DATE_FILTERS.CUSTOM ? (
            <>
              <input
                type="date"
                value={customFromDate}
                onChange={(e) => setCustomFromDate(e.target.value)}
                style={{ ...smallSelect, minWidth: 145 }}
              />

              <span style={{ fontSize: 11.5, color: "#64748b", fontWeight: 900 }}>
                to
              </span>

              <input
                type="date"
                value={customToDate}
                onChange={(e) => setCustomToDate(e.target.value)}
                style={{ ...smallSelect, minWidth: 145 }}
              />
            </>
          ) : null}

          <span style={pillStyle(metrics.completedCount ? "good" : "default")}>
            Showing: {metrics.recentCompleted.length}
          </span>
        </div>
      </div>

      <div style={metricGrid}>
        <div style={metricCardStyle("blue")}>
          <div style={metricLabel}>Technician</div>
          <div
            style={{
              ...metricValue,
              fontSize: 16,
              lineHeight: 1.1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={techName}
          >
            {techName}
          </div>
          <div style={metricSub}>{dateWindow.label}</div>
        </div>

        <div style={metricCardStyle()}>
          <div style={metricLabel}>Completed ROs</div>
          <div style={metricValue}>{metrics.completedCount}</div>
          <div style={metricSub}>Completed during {dateWindow.label}.</div>
        </div>

        <div style={metricCardStyle()}>
          <div style={metricLabel}>Flagged Hours</div>
          <div style={metricValue}>{formatHours(metrics.flaggedHours)}</div>
          <div style={metricSub}>Required at completion.</div>
        </div>

        <div style={metricCardStyle()}>
          <div style={metricLabel}>Worked Hours</div>
          <div style={metricValue}>{formatHours(metrics.workedHours)}</div>
          <div style={metricSub}>Net time tracked.</div>
        </div>
      </div>

      <div style={metricGrid}>
        <div style={metricCardStyle(efficiencyTone)}>
          <div style={metricLabel}>Efficiency</div>
          <div style={metricValue}>
            {metrics.workedHours > 0 ? formatEfficiency(metrics.efficiency) : "—"}
          </div>
          <div style={metricSub}>Flagged ÷ worked.</div>
        </div>

        <div style={metricCardStyle()}>
          <div style={metricLabel}>Avg Flagged / RO</div>
          <div style={metricValue}>{formatHours(metrics.avgFlagged)}</div>
          <div style={metricSub}>Average sold/flagged hours.</div>
        </div>

        <div style={metricCardStyle()}>
          <div style={metricLabel}>Avg Worked / RO</div>
          <div style={metricValue}>{formatHours(metrics.avgWorked)}</div>
          <div style={metricSub}>Average technician time.</div>
        </div>

        <div style={metricCardStyle()}>
          <div style={metricLabel}>Recent Records</div>
          <div style={metricValue}>{metrics.recentCompleted.length}</div>
          <div style={metricSub}>Visible completed records.</div>
        </div>
      </div>

      <div style={card}>
        <div style={sectionTitle}>Technician Summary</div>
        <div style={summaryGrid}>
          <SummaryStat label="Technician" value={techName} />
          <SummaryStat label="Date range" value={dateWindow.label} />
          <SummaryStat label="Completed ROs" value={metrics.completedCount} />
          <SummaryStat label="Flagged hours" value={formatHours(metrics.flaggedHours)} />
          <SummaryStat label="Worked hours" value={formatHours(metrics.workedHours)} />
          <SummaryStat
            label="Efficiency"
            value={metrics.workedHours > 0 ? formatEfficiency(metrics.efficiency) : "—"}
          />
          <SummaryStat label="Avg flagged / RO" value={formatHours(metrics.avgFlagged)} />
          <SummaryStat label="Avg worked / RO" value={formatHours(metrics.avgWorked)} />
        </div>
      </div>

      <div style={card}>
        <div style={sectionTitle}>Work Mix</div>

        <div style={{ display: "grid", gap: 7 }}>
          <MixBar
            label="Maintenance"
            count={metrics.workMixCounts.Maintenance}
            total={metrics.completedCount}
          />
          <MixBar
            label="Diagnosis"
            count={metrics.workMixCounts.Diagnosis}
            total={metrics.completedCount}
          />
          <MixBar
            label="Other"
            count={metrics.workMixCounts.Other}
            total={metrics.completedCount}
          />
        </div>
      </div>

      <div style={card}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "baseline",
            marginBottom: 7,
          }}
        >
          <div style={sectionTitle}>Recent Completed</div>

          <div
            style={{
              fontSize: 11.5,
              color: "rgba(15,23,42,0.55)",
              fontWeight: 750,
            }}
          >
            Showing {metrics.recentCompleted.length}
          </div>
        </div>

        {metrics.recentCompleted.length === 0 ? (
          <EmptyState message={`No completed ROs found for ${dateWindow.label}.`} />
        ) : (
          <div style={{ display: "grid", gap: 6 }}>
            {metrics.recentCompleted.map((ro) => (
              <CompletedRO key={ro.id || ro.roNumber} ro={ro} />
            ))}
          </div>
        )}
      </div>

      <style>
        {`
          @media (max-width: 1180px) {
            div[style*="grid-template-columns: repeat(4"] {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
          }

          @media (max-width: 720px) {
            div[style*="grid-template-columns: repeat(4"] {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </div>
  );
}

/* ===============================
   Small Components
================================ */
function SummaryStat({ label, value, style }) {
  return (
    <div
      style={{
        border: "1px solid rgba(15,23,42,0.07)",
        borderRadius: 12,
        padding: "7px 9px",
        background: "#ffffff",
        minWidth: 0,
        ...style,
      }}
    >
      <div
        style={{
          fontSize: 10.2,
          color: "rgba(15,23,42,0.44)",
          fontWeight: 850,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: 3,
        }}
      >
        {label}
      </div>
      <div
        style={{
          color: "rgba(15,23,42,0.80)",
          fontSize: 12.2,
          fontWeight: 800,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={safeString(value)}
      >
        {value}
      </div>
    </div>
  );
}

function MixBar({ label, count, total }) {
  const pct = percent(count, total);

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 11.5,
          marginBottom: 5,
          color: "rgba(15,23,42,0.74)",
        }}
      >
        <b>{label}</b>
        <span>
          {count || 0} • {pct}%
        </span>
      </div>

      <ProgressBar value={pct} />
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div
      style={{
        padding: 10,
        border: "1px dashed #cbd5e1",
        borderRadius: 12,
        color: "rgba(15,23,42,0.55)",
        background: "#f8fafc",
        fontSize: 11.5,
      }}
    >
      {message}
    </div>
  );
}

function CompletedRO({ ro }) {
  const tag = getTag(ro);
  const worked = getWorkedHours(ro);
  const flagged = getFlaggedHours(ro);
  const workMix = getWorkMixKey(ro);

  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 8,
        background: "#ffffff",
        display: "flex",
        justifyContent: "space-between",
        gap: 10,
        alignItems: "flex-start",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            gap: 7,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <b style={{ color: "rgba(15,23,42,0.94)", fontSize: 13.25, fontWeight: 800 }}>
            RO #{safeString(ro?.roNumber || ro?.ro_number || "—")}
          </b>

          {tag ? <span style={pillStyle()}>Tag #{tag}</span> : null}
          <span style={pillStyle("good")}>Completed</span>
          <span style={pillStyle("blue")}>{workMixLabel(workMix)}</span>
        </div>

        <div style={{ marginTop: 3, fontSize: 11.5, color: "rgba(15,23,42,0.74)" }}>
          <b>{safeString(ro?.customerName || "—")}</b>
          {" — "}
          {safeString(ro?.vehicle || "—")}
        </div>

        <div
          style={{
            marginTop: 2,
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            fontSize: 10.8,
            color: "rgba(15,23,42,0.55)",
          }}
        >
          <span>
            Completed:{" "}
            <b style={{ color: "rgba(15,23,42,0.70)" }}>
              {formatDateTime(getCompletedAt(ro))}
            </b>
          </span>
          <span>
            Flagged:{" "}
            <b style={{ color: "rgba(15,23,42,0.70)" }}>
              {formatHours(flagged)}h
            </b>
          </span>
          <span>
            Worked:{" "}
            <b style={{ color: "rgba(15,23,42,0.70)" }}>
              {formatHours(worked)}h
            </b>
          </span>
        </div>
      </div>
    </div>
  );
}
